import {
  GoogleMap,
  Marker,
  Circle,
  OverlayView,
} from '@react-google-maps/api';
import { capitalize } from "../../../assets/helpers/funciones"
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AltButton, ActionButton } from "../Buttons/Buttons";
import {
  MAP_CENTER,
  MAP_ZOOM,
  CITIES,
  formatStreetAddress,
  sortDevicesByDistanceToPoint,
  groupDevicesByProximity,
  getClusterHeatZone,
  CLUSTER_COLORS,
  hexToRgba,
  PROXIMITY_GROUP_THRESHOLD_M,
  getNonOverlappingLabelOffsets,
} from "./allDevicesMapConfig";
import craneLogo from '../../../images/craneLogo.png';
import bikeFollowing from '../../../images/bikeFollowing.svg';
import './Map.scss';

const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };
const MAP_OPTIONS = {
  zoomControl: true,
  mapTypeControl: false,
  fullscreenControl: true,
};
const MARKER_OPTIONS = { optimized: false };

const TYPE_GRUA = 'Grua';
const TYPE_MOTO = 'Asistente Vial';

const ICON_URL_BY_TYPE = {
  [TYPE_GRUA]: craneLogo,
  [TYPE_MOTO]: bikeFollowing,
};

const coordsKey = (lat, lng) =>
  `v4:${Number(lat).toFixed(5)},${Number(lng).toFixed(5)}`;

const getDeviceKey = (device) => device?.id ?? device?.name ?? null;

export const AllDevicesMap = (props) => {
  const { data, refresh } = props;
  const mapRef = useRef(null);
  const mapCenterRef = useRef(MAP_CENTER);
  const markerIconBaseRef = useRef(null);
  const addressCacheRef = useRef({});
  const [mapCenter, setMapCenter] = useState(MAP_CENTER);
  const [sortCenter, setSortCenter] = useState(MAP_CENTER);
  const [mapZoom, setMapZoom] = useState(MAP_ZOOM);
  const [iconsReady, setIconsReady] = useState(false);
  const [legendOpen, setLegendOpen] = useState(true);
  const [labelsVisible, setLabelsVisible] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [addressByKey, setAddressByKey] = useState({});
  const [loadingAddressKey, setLoadingAddressKey] = useState(null);
  const [visibleTypes, setVisibleTypes] = useState({
    [TYPE_GRUA]: true,
    [TYPE_MOTO]: true,
  });

  const selectedIndex = useMemo(() => {
    if (selectedKey == null) return null;
    const idx = (data || []).findIndex((d) => getDeviceKey(d) === selectedKey);
    return idx === -1 ? null : idx;
  }, [data, selectedKey]);

  const typeCounts = useMemo(() => {
    return (data || []).reduce(
      (acc, d) => {
        if (d?.type === TYPE_GRUA) acc.gruas += 1;
        if (d?.type === TYPE_MOTO) acc.motos += 1;
        return acc;
      },
      { gruas: 0, motos: 0 }
    );
  }, [data]);

  const legendGroups = useMemo(() => {
    const items = (data || [])
      .map((d, i) => ({ d, i }))
      .filter(({ d }) => visibleTypes[d?.type] !== false);
    const ordered = sortDevicesByDistanceToPoint(
      items,
      sortCenter.lat,
      sortCenter.lng
    );
    const groups = groupDevicesByProximity(ordered, PROXIMITY_GROUP_THRESHOLD_M);

    let clusterIndex = 0;
    return groups.map((members, groupIndex) => {
      const isCluster = members.length >= 2;
      const color = isCluster
        ? CLUSTER_COLORS[clusterIndex++ % CLUSTER_COLORS.length]
        : null;
      const heatZone = isCluster ? getClusterHeatZone(members) : null;

      return {
        members,
        groupIndex,
        isCluster,
        color,
        heatZone,
      };
    });
  }, [data, visibleTypes, sortCenter]);

  const labelOffsets = useMemo(() => {
    const items = (data || [])
      .map((d, i) => ({
        i,
        lat: d?.lat,
        lng: d?.lng,
        name: capitalize(d?.provider || d?.name || ""),
      }))
      .filter(
        (item) =>
          visibleTypes[data?.[item.i]?.type] !== false &&
          item.name &&
          Number.isFinite(item.lat) &&
          Number.isFinite(item.lng)
      );

    return getNonOverlappingLabelOffsets(items, mapZoom);
  }, [data, visibleTypes, mapZoom]);

  const toggleTypeVisibility = (type) => {
    setVisibleTypes((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const isTypeVisible = (type) => visibleTypes[type] !== false;

  const getMarkerIcon = useCallback((device) => {
    const url = ICON_URL_BY_TYPE[device?.type] || device?.icon?.path;
    const base = markerIconBaseRef.current;
    if (!url || !base) return undefined;
    return {
      url,
      scaledSize: base.scaledSize,
      anchor: base.anchor,
    };
  }, []);

  const fetchAddress = useCallback(async (lat, lng) => {
    const key = coordsKey(lat, lng);
    if (addressCacheRef.current[key]) {
      return addressCacheRef.current[key];
    }
    if (!window.google?.maps?.Geocoder) {
      return null;
    }

    setLoadingAddressKey(key);
    try {
      const geocoder = new window.google.maps.Geocoder();
      const response = await geocoder.geocode({ location: { lat, lng } });
      const address = formatStreetAddress(response, lat, lng);
      addressCacheRef.current[key] = address;
      setAddressByKey((prev) => ({ ...prev, [key]: address }));
      return address;
    } catch {
      const fallback = 'Dirección no disponible';
      addressCacheRef.current[key] = fallback;
      setAddressByKey((prev) => ({ ...prev, [key]: fallback }));
      return fallback;
    } finally {
      setLoadingAddressKey((prev) => (prev === key ? null : prev));
    }
  }, []);

  const lockCenter = useCallback((map = mapRef.current) => {
    if (!map) return;
    map.setCenter(mapCenterRef.current);
    map.setZoom(MAP_ZOOM);
  }, []);

  const updateSortCenterFromMap = useCallback((map = mapRef.current) => {
    if (!map?.getCenter) return;
    const center = map.getCenter();
    if (!center) return;

    const next = { lat: center.lat(), lng: center.lng() };
    setSortCenter((prev) => {
      if (
        Math.abs(prev.lat - next.lat) < 1e-6 &&
        Math.abs(prev.lng - next.lng) < 1e-6
      ) {
        return prev;
      }
      return next;
    });

    if (typeof map.getZoom === "function") {
      const zoom = map.getZoom();
      setMapZoom((prev) => (prev === zoom ? prev : zoom));
    }
  }, []);

  const handleOnLoad = useCallback((map) => {
    mapRef.current = map;
    markerIconBaseRef.current = {
      scaledSize: new window.google.maps.Size(30, 30),
      anchor: new window.google.maps.Point(15, 15),
    };
    setIconsReady(true);
    window.google.maps.event.trigger(map, 'resize');
    lockCenter(map);
    updateSortCenterFromMap(map);
  }, [lockCenter, updateSortCenterFromMap]);

  useEffect(() => {
    lockCenter();
    const t = setTimeout(() => {
      lockCenter();
      updateSortCenterFromMap();
    }, 50);
    return () => clearTimeout(t);
  }, [lockCenter, updateSortCenterFromMap]);

  useEffect(() => {
    if (selectedKey == null) return;
    const selected = (data || []).find((d) => getDeviceKey(d) === selectedKey);
    if (!selected || visibleTypes[selected?.type] === false) {
      setSelectedKey(null);
    }
  }, [data, selectedKey, visibleTypes]);

  useEffect(() => {
    if (selectedIndex == null) return;
    const device = data?.[selectedIndex];
    if (!device || !Number.isFinite(device.lat) || !Number.isFinite(device.lng)) {
      return;
    }
    const key = coordsKey(device.lat, device.lng);
    if (addressCacheRef.current[key]) return;
    fetchAddress(device.lat, device.lng);
  }, [selectedIndex, data, fetchAddress]);

  const selectCity = (city) => {
    const center = { lat: city.lat, lng: city.lng };
    mapCenterRef.current = center;
    setMapCenter(center);
    setSortCenter(center);
    lockCenter();
  };

  const focusDriver = (index) => {
    const device = data?.[index];
    const key = getDeviceKey(device);
    setSelectedKey((prev) => (prev === key ? null : key));
    if (device && mapRef.current && Number.isFinite(device.lat) && Number.isFinite(device.lng)) {
      mapRef.current.panTo({ lat: device.lat, lng: device.lng });
    }
  };

  const onReload = () => {
    refresh({ silent: true });
  };

  const getDeviceAddressLabel = (device, isSelected) => {
    if (!isSelected || !Number.isFinite(device?.lat) || !Number.isFinite(device?.lng)) {
      return null;
    }
    const key = coordsKey(device.lat, device.lng);
    if (loadingAddressKey === key && !addressByKey[key]) {
      return 'Cargando dirección...';
    }
    return addressByKey[key] || null;
  };

  if (data?.length > 0) {
    return (
      <div className="devices-map-root">
        <div className="devices-type-counters">
          <h4 style={{ marginBottom: '0px' }}>Choferes activos</h4>
          <button
            type="button"
            className={`devices-type-counter${isTypeVisible(TYPE_GRUA) ? ' is-active' : ''}`}
            onClick={() => toggleTypeVisibility(TYPE_GRUA)}
          >
            <img src={craneLogo} alt="Grúas" />
            <span>{typeCounts.gruas}</span>
          </button>
          <button
            type="button"
            className={`devices-type-counter${isTypeVisible(TYPE_MOTO) ? ' is-active' : ''}`}
            onClick={() => toggleTypeVisibility(TYPE_MOTO)}
          >
            <img src={bikeFollowing} alt="Motos" />
            <span>{typeCounts.motos}</span>
          </button>
        </div>
        <div className={`devices-map-layout${legendOpen ? ' has-legend' : ''}`}>
          <div className="devices-map-main">
            <GoogleMap
              center={mapCenter}
              zoom={MAP_ZOOM}
              onLoad={handleOnLoad}
              onIdle={() => updateSortCenterFromMap()}
              mapContainerStyle={MAP_CONTAINER_STYLE}
              mapContainerClassName="devices-google-map"
              options={MAP_OPTIONS}
            >
              {legendGroups.map(({ groupIndex, isCluster, color, heatZone }) => {
                if (!isCluster || !color || !heatZone) return null;

                return (
                  <Fragment key={`heat-${groupIndex}`}>
                    <Circle
                      center={heatZone.center}
                      radius={heatZone.radius * 1.4}
                      options={{
                        strokeOpacity: 0,
                        fillColor: color,
                        fillOpacity: 0.08,
                        clickable: false,
                        zIndex: 0,
                      }}
                    />
                    <Circle
                      center={heatZone.center}
                      radius={heatZone.radius}
                      options={{
                        strokeColor: color,
                        strokeOpacity: 0.7,
                        strokeWeight: 2,
                        fillColor: color,
                        fillOpacity: 0.22,
                        clickable: false,
                        zIndex: 1,
                      }}
                    />
                  </Fragment>
                );
              })}
              {iconsReady &&
                (data || []).map((d, i) => {
                  if (!isTypeVisible(d?.type)) return null;

                  const markerKey = d?.id ?? d?.name ?? i;
                  const position = { lat: d?.lat, lng: d?.lng };
                  const driverName = capitalize(d?.provider || d?.name || '');
                  const showNameLabel = labelsVisible && Boolean(driverName);
                  const labelOffset = labelOffsets[i] || { x: 0, y: 20 };

                  return (
                    <Fragment key={markerKey}>
                      <Marker
                        onClick={() => focusDriver(i)}
                        position={position}
                        label={''}
                        icon={getMarkerIcon(d)}
                        options={MARKER_OPTIONS}
                        zIndex={selectedIndex === i ? 999 : 1}
                      />
                      {showNameLabel && (
                        <OverlayView
                          key={`label-${markerKey}-${labelOffset.x}-${labelOffset.y}-${mapZoom}`}
                          position={position}
                          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                          getPixelPositionOffset={(width) => ({
                            x: labelOffset.x - width / 2,
                            y: labelOffset.y,
                          })}
                        >
                          <button
                            type="button"
                            className="devices-marker-label"
                            onClick={() => focusDriver(i)}
                          >
                            {driverName}
                          </button>
                        </OverlayView>
                      )}
                    </Fragment>
                  );
                })}
            </GoogleMap>
          </div>
          {legendOpen && (
            <aside className="devices-legend-list">
              <h5>Choferes</h5>
              <ul>
                {legendGroups.map(({ members, groupIndex, isCluster, color }) => (
                  <li
                    key={members.map(({ i }) => i).join('-') || groupIndex}
                    className={
                      isCluster
                        ? 'devices-legend-group is-cluster'
                        : 'devices-legend-group is-single'
                    }
                    style={
                      isCluster && color
                        ? {
                            '--cluster-color': color,
                            '--cluster-fill': hexToRgba(color, 0.14),
                            '--cluster-border': hexToRgba(color, 0.4),
                            '--cluster-hover': hexToRgba(color, 0.18),
                            '--cluster-selected': hexToRgba(color, 0.28),
                          }
                        : undefined
                    }
                  >
                    {isCluster && (
                      <span className="devices-legend-group-label">
                        Cerca ({members.length})
                      </span>
                    )}
                    <ul>
                      {members.map(({ d, i }) => {
                        const isSelected = selectedIndex === i;
                        const addressLabel = getDeviceAddressLabel(d, isSelected);

                        return (
                          <li key={d?.id ?? d?.name ?? i}>
                            <button
                              type="button"
                              className={`devices-legend-item${isSelected ? ' is-selected' : ''}`}
                              onClick={() => focusDriver(i)}
                            >
                              <img
                                src={ICON_URL_BY_TYPE[d?.type] || d?.icon?.path}
                                alt=""
                              />
                              <span className="devices-legend-item-text">
                                <span className="devices-legend-item-name">
                                  {capitalize(d?.provider || d?.name || 'Sin nombre')}
                                </span>
                                {addressLabel && (
                                  <span className="devices-address-text">{addressLabel}</span>
                                )}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                ))}
              </ul>
            </aside>
          )}
        </div>
        <div style={{ marginTop: '16px' }} className="buttons-center-container">
          {CITIES.map((city) => (
            <AltButton
              key={city.name}
              label={city.name}
              action={() => selectCity(city)}
            />
          ))}
        </div>
        <div style={{ marginTop: '16px' }} className="buttons-center-container">
          <AltButton
            label={legendOpen ? "Ocultar leyenda" : "Leyenda"}
            action={() => setLegendOpen((open) => !open)}
          />
          <AltButton
            label={labelsVisible ? "Ocultar etiquetas" : "Mostrar etiquetas"}
            action={() => setLabelsVisible((visible) => !visible)}
          />
          <ActionButton label={"Actualizar"} action={() => onReload()} />
        </div>
      </div>
    )
  } else {
    return null
  }
}
